import { z } from 'zod';
import 'clsx';
import 'date-fns';
import 'date-fns-tz';
import 'tailwind-merge';

// src/validation/index.ts

// src/constants/index.ts
var COMPANY_ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  DRIVER: "driver"
};
var RESERVATION_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  ASSIGNED: "assigned",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled"
};
var PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PAID: "paid",
  PARTIAL: "partial"
};
var PAYMENT_METHODS = {
  CASH: "cash",
  VENMO: "venmo",
  ZELLE: "zelle",
  PAYPAL: "paypal",
  CHECK: "check",
  BANK_TRANSFER: "bank_transfer",
  CREDIT_CARD: "credit_card",
  OTHER: "other"};
var RIDE_TYPES = {
  POINT_TO_POINT: "point-to-point",
  HOURLY: "hourly",
  CUSTOM: "custom"
};
var RIDE_SUB_TYPES = {
  ARRIVAL: "arrival",
  DEPARTURE: "departure"
};
var DOCUMENT_TYPES = {
  DRIVER_LICENSE: "driver_license",
  INSURANCE: "insurance",
  VEHICLE_REGISTRATION: "vehicle_registration",
  OTHER: "other"
};
var DOCUMENT_ACCESS_STATUS = {
  PENDING: "pending",
  GRANTED: "granted",
  DENIED: "denied",
  REVOKED: "revoked"
};
var DOCUMENT_REVIEW_STATUS = {
  APPROVED: "approved",
  REJECTED: "rejected"
};
var DRIVER_APPROVAL_STATUS = {
  PENDING_DOCUMENTS: "pending_documents",
  DOCUMENTS_RECEIVED: "documents_received",
  APPROVED: "approved",
  REJECTED: "rejected",
  MORE_INFO_NEEDED: "more_info_needed"
};
var FLEET_SIZES = {
  ONE: "1",
  TWO_TO_FIVE: "2-5",
  SIX_TO_TEN: "6-10",
  ELEVEN_TO_TWENTYFIVE: "11-25",
  TWENTYSIX_PLUS: "26+"
};
var AVAILABILITY_STATUS = {
  AVAILABLE: "available",
  UNAVAILABLE: "unavailable",
  BUSY: "busy"
};
var INQUIRY_STATUS = {
  NEW: "new",
  CONTACTED: "contacted",
  CONVERTED: "converted",
  CLOSED: "closed"
};
var INQUIRY_PLAN_OPTIONS = {
  BUSINESS: "business",
  BUSINESS_PLUS: "business_plus",
  BUSINESS_ULTIMATE: "business_ultimate",
  HELP_ME_CHOOSE: "help_me_choose"
};

// src/utils/index.ts
var RESERVED_SLUGS = [
  // System routes
  "admin",
  "api",
  "app",
  "auth",
  "book",
  "booking",
  "bookings",
  "dashboard",
  "login",
  "logout",
  "register",
  "signup",
  "signin",
  "settings",
  "account",
  "profile",
  "user",
  "users",
  // Subdomains / infrastructure
  "www",
  "mail",
  "email",
  "ftp",
  "cdn",
  "assets",
  "static",
  "media",
  "images",
  "img",
  "files",
  "docs",
  "help",
  "support",
  "status",
  "blog",
  // Platform terms
  "ridereserv",
  "ride-reserv",
  "driver",
  "drivers",
  "company",
  "companies",
  "fleet",
  "platform",
  "public",
  "private",
  // Common words that might cause confusion
  "test",
  "demo",
  "example",
  "sample",
  "null",
  "undefined",
  "true",
  "false",
  "new",
  "edit",
  "delete",
  "create",
  "update"
];
function isReservedSlug(slug) {
  return RESERVED_SLUGS.includes(slug.toLowerCase());
}

// src/validation/index.ts
var uuidSchema = z.guid();
var emailSchema = z.email("Please enter a valid email address").max(255, "Email must be 255 characters or less");
var phoneSchema = z.string("Please enter a phone number").regex(/^[\d\s\-+()]+$/, "Phone number can only contain digits, spaces, and + - ( )").min(10, "Phone number must be at least 10 digits").max(20, "Phone number must be 20 characters or less");
var timezoneSchema = z.string().refine(
  (tz) => {
    try {
      Intl.DateTimeFormat(void 0, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid timezone" }
);
var slugSchema = z.string().min(2, "Slug must be at least 2 characters").max(100, "Slug must be 100 characters or less").regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens").refine((slug) => !slug.startsWith("-") && !slug.endsWith("-"), {
  message: "Slug cannot start or end with a hyphen"
}).refine((slug) => !slug.includes("--"), {
  message: "Slug cannot contain consecutive hyphens"
}).refine((slug) => !isReservedSlug(slug), {
  message: "This URL is reserved and cannot be used"
});
var hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Invalid hex color format");
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20)
});
var PREFERENCES_MAX_DEPTH = 8;
var PREFERENCES_MAX_BYTES = 16 * 1024;
function exceedsDepth(value, max, depth = 0) {
  if (depth > max) return true;
  if (Array.isArray(value)) {
    return value.some((v) => exceedsDepth(v, max, depth + 1));
  }
  if (value !== null && typeof value === "object") {
    return Object.values(value).some(
      (v) => exceedsDepth(v, max, depth + 1)
    );
  }
  return false;
}
var boundedPreferences = z.record(z.string(), z.unknown()).refine(
  (p) => !exceedsDepth(p, PREFERENCES_MAX_DEPTH) && JSON.stringify(p).length <= PREFERENCES_MAX_BYTES,
  { message: "Preferences are too large or nested too deeply" }
);
var createUserSchema = z.object({
  email: emailSchema,
  name: z.string().min(1).max(255),
  phone: phoneSchema.optional(),
  preferences: boundedPreferences.optional(),
  consentMarketing: z.boolean().optional().default(false),
  consentDataSharing: z.boolean().optional().default(false)
});
var updateUserSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  phone: phoneSchema.nullable().optional(),
  preferences: boundedPreferences.optional(),
  consentMarketing: z.boolean().optional(),
  consentDataSharing: z.boolean().optional()
});
var companySettingsSchema = z.object({
  minHoursNotice: z.number().int().min(0).max(168).optional(),
  maxAdvanceBookingDays: z.number().int().min(1).max(365).optional(),
  autoConfirmReservations: z.boolean().optional(),
  requireGuestPhone: z.boolean().optional(),
  serviceLevelsEnabled: z.boolean().optional()
});
var companyBookingPolicySchema = z.object({
  minHoursNotice: z.number().int().min(0).max(168).optional(),
  maxAdvanceBookingDays: z.number().int().min(1).max(365).optional(),
  autoConfirmReservations: z.boolean().optional(),
  requireGuestPhone: z.boolean().optional()
}).strict();
var COMPANY_BOOKING_POLICY_DEFAULTS = {
  minHoursNotice: 2,
  maxAdvanceBookingDays: 90,
  autoConfirmReservations: false,
  requireGuestPhone: true
};
function resolveBookingPolicy(settings) {
  const s = settings && typeof settings === "object" ? settings : {};
  const num = (v, fallback) => typeof v === "number" && Number.isFinite(v) ? v : fallback;
  const bool = (v, fallback) => typeof v === "boolean" ? v : fallback;
  return {
    minHoursNotice: num(s["minHoursNotice"], COMPANY_BOOKING_POLICY_DEFAULTS.minHoursNotice),
    maxAdvanceBookingDays: num(
      s["maxAdvanceBookingDays"],
      COMPANY_BOOKING_POLICY_DEFAULTS.maxAdvanceBookingDays
    ),
    autoConfirmReservations: bool(
      s["autoConfirmReservations"],
      COMPANY_BOOKING_POLICY_DEFAULTS.autoConfirmReservations
    ),
    requireGuestPhone: bool(
      s["requireGuestPhone"],
      COMPANY_BOOKING_POLICY_DEFAULTS.requireGuestPhone
    )
  };
}
var companyLocationSettingsSchema = z.object({
  precision: z.enum(["exact", "coarse"]).optional(),
  retentionDays: z.number().int().min(1).max(3650).nullable().optional(),
  customerRouteAfterCompletion: z.boolean().optional(),
  fleetMapEnabled: z.boolean().optional()
}).strict();
var COMPANY_LOCATION_SETTINGS_DEFAULTS = {
  precision: "exact",
  retentionDays: null,
  customerRouteAfterCompletion: true,
  fleetMapEnabled: true
};
function resolveLocationSettings(settings) {
  const root = settings && typeof settings === "object" ? settings : {};
  const s = root["location"] && typeof root["location"] === "object" ? root["location"] : {};
  const bool = (v, fallback) => typeof v === "boolean" ? v : fallback;
  const retentionRaw = s["retentionDays"];
  const retentionDays = typeof retentionRaw === "number" && Number.isFinite(retentionRaw) && retentionRaw > 0 ? Math.floor(retentionRaw) : null;
  return {
    precision: s["precision"] === "coarse" ? "coarse" : "exact",
    retentionDays,
    customerRouteAfterCompletion: bool(
      s["customerRouteAfterCompletion"],
      COMPANY_LOCATION_SETTINGS_DEFAULTS.customerRouteAfterCompletion
    ),
    fleetMapEnabled: bool(s["fleetMapEnabled"], COMPANY_LOCATION_SETTINGS_DEFAULTS.fleetMapEnabled)
  };
}
var createCompanySchema = z.object({
  name: z.string().min(1).max(255),
  slug: slugSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
  address: z.string().max(500).optional(),
  timezone: timezoneSchema.optional().default("America/New_York"),
  settings: companySettingsSchema.optional()
});
var embedOriginSchema = z.string().refine(
  (value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && url.origin === value;
    } catch {
      return false;
    }
  },
  'Each origin must be a full https:// origin with no path, query, or fragment, e.g. "https://yoursite.com"'
);
var updateCompanySchema = z.object({
  name: z.string().min(1).max(255).optional(),
  email: emailSchema.optional(),
  phone: phoneSchema.nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  timezone: timezoneSchema.optional(),
  settings: companySettingsSchema.optional(),
  showPricingToCustomers: z.boolean().optional(),
  driverReminderEmail: z.boolean().optional(),
  driverReminderSms: z.boolean().optional(),
  // Allowlist of origins permitted to embed the booking widget (#458).
  // Each entry must be a full https:// origin (no path/query/fragment).
  allowedEmbedOrigins: z.array(embedOriginSchema).optional()
});
var updateCompanySlugSchema = z.object({
  slug: slugSchema
});
var companyRoleSchema = z.enum([
  COMPANY_ROLES.ADMIN,
  COMPANY_ROLES.MANAGER,
  COMPANY_ROLES.DRIVER
]);
var createCompanyMemberSchema = z.object({
  userId: uuidSchema,
  companyId: uuidSchema,
  roles: z.array(companyRoleSchema).min(1)
});
var updateCompanyMemberSchema = z.object({
  roles: z.array(companyRoleSchema).min(1).optional(),
  isActive: z.boolean().optional()
});
var invitationMetadataSchema = z.object({
  message: z.string().max(500).optional(),
  requirements: z.array(z.string()).optional()
});
var createInvitationSchema = z.object({
  email: emailSchema,
  roles: z.array(companyRoleSchema).min(1),
  expiresInDays: z.number().int().min(1).max(30).optional(),
  metadata: invitationMetadataSchema.optional()
});
var invitationStatusSchema = z.enum(["pending", "accepted", "cancelled", "expired"]);
var listInvitationsSchema = z.object({
  status: invitationStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional()
});
var serviceLevelFeaturesSchema = z.object({
  wifi: z.boolean().optional(),
  refreshments: z.boolean().optional(),
  childSeat: z.boolean().optional()
});
var serviceLevelRestrictionsSchema = z.object({
  maxDistance: z.number().positive().optional(),
  allowedAreas: z.array(z.string()).optional()
});
var createServiceLevelSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1e3).optional(),
  sortOrder: z.number().int().min(0).optional().default(0),
  isDefault: z.boolean().optional().default(false),
  minPrice: z.number().min(0),
  hourlyRate: z.number().min(0).optional(),
  maxPassengers: z.number().int().min(1).optional(),
  minPassengers: z.number().int().min(1).optional(),
  maxLuggage: z.number().int().min(0).optional(),
  restrictions: serviceLevelRestrictionsSchema.optional(),
  features: serviceLevelFeaturesSchema.optional(),
  settings: z.record(z.string(), z.unknown()).optional()
});
var updateServiceLevelSchema = createServiceLevelSchema.partial();
var SERVICE_LEVEL_FEATURE_KEYS = [
  "wifi",
  "water",
  "charger",
  "childSeat",
  "champagne",
  "entertainment",
  "wheelchairAccessible"
];
var serviceLevelFeaturesObjectSchema = z.strictObject({
  wifi: z.boolean().optional(),
  water: z.boolean().optional(),
  charger: z.boolean().optional(),
  childSeat: z.boolean().optional(),
  champagne: z.boolean().optional(),
  entertainment: z.boolean().optional(),
  wheelchairAccessible: z.boolean().optional()
});
var SERVICE_LEVEL_MAX_PRICE = 1e4;
var SERVICE_LEVEL_MIN_PASSENGERS = 1;
var SERVICE_LEVEL_MAX_PASSENGERS = 50;
var SERVICE_LEVEL_MAX_LUGGAGE = 30;
var serviceLevelSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or fewer"),
  description: z.string().trim().max(500, "Description must be 500 characters or fewer").optional(),
  minPrice: z.number({ error: "Minimum price is required" }).min(0, "Minimum price cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Minimum price is too high"),
  hourlyRate: z.number().min(0, "Hourly rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Hourly rate is too high").nullable().optional(),
  perMileRate: z.number().min(0, "Per-mile rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-mile rate is too high").nullable().optional(),
  perMinuteRate: z.number().min(0, "Per-minute rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-minute rate is too high").nullable().optional(),
  minPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Minimum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Minimum passengers cannot exceed 50").optional(),
  maxPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Maximum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Maximum passengers cannot exceed 50").optional(),
  maxLuggage: z.number().int("Luggage count must be a whole number").min(0, "Luggage count cannot be negative").max(SERVICE_LEVEL_MAX_LUGGAGE, "Luggage count cannot exceed 30").optional(),
  features: serviceLevelFeaturesObjectSchema.optional(),
  sortOrder: z.number().int("Sort order must be a whole number").min(0, "Sort order cannot be negative").optional().default(0),
  isDefault: z.boolean().optional()
}).superRefine((data, ctx) => {
  if (data.minPassengers !== void 0 && data.maxPassengers !== void 0 && data.minPassengers > data.maxPassengers) {
    ctx.addIssue({
      code: "custom",
      message: "Minimum passengers cannot exceed maximum passengers",
      path: ["minPassengers"]
    });
  }
});
var serviceLevelUpdateSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or fewer").optional(),
  description: z.string().trim().max(500, "Description must be 500 characters or fewer").nullable().optional(),
  minPrice: z.number().min(0, "Minimum price cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Minimum price is too high").optional(),
  hourlyRate: z.number().min(0, "Hourly rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Hourly rate is too high").nullable().optional(),
  perMileRate: z.number().min(0, "Per-mile rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-mile rate is too high").nullable().optional(),
  perMinuteRate: z.number().min(0, "Per-minute rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-minute rate is too high").nullable().optional(),
  minPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Minimum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Minimum passengers cannot exceed 50").nullable().optional(),
  maxPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Maximum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Maximum passengers cannot exceed 50").nullable().optional(),
  maxLuggage: z.number().int("Luggage count must be a whole number").min(0, "Luggage count cannot be negative").max(SERVICE_LEVEL_MAX_LUGGAGE, "Luggage count cannot exceed 30").nullable().optional(),
  features: serviceLevelFeaturesObjectSchema.optional(),
  sortOrder: z.number().int("Sort order must be a whole number").min(0, "Sort order cannot be negative").optional(),
  isDefault: z.boolean().optional()
}).superRefine((data, ctx) => {
  if (data.minPassengers !== void 0 && data.minPassengers !== null && data.maxPassengers !== void 0 && data.maxPassengers !== null && data.minPassengers > data.maxPassengers) {
    ctx.addIssue({
      code: "custom",
      message: "Minimum passengers cannot exceed maximum passengers",
      path: ["minPassengers"]
    });
  }
});
var updateCompanyRideTypesSchema = z.object({
  enabledRideTypeIds: z.array(uuidSchema).max(100)
});
var rideTypeSchema = z.enum([
  RIDE_TYPES.POINT_TO_POINT,
  RIDE_TYPES.HOURLY,
  RIDE_TYPES.CUSTOM
]);
var stopRoleSchema = z.enum(["pickup", "dropoff"]);
var stopInputSchema = z.object({
  role: stopRoleSchema,
  address: z.string("Address is required").min(1, "Address is required").max(500, "Address must be 500 characters or less"),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional()
});
var rideSubTypeSchema = z.enum([RIDE_SUB_TYPES.ARRIVAL, RIDE_SUB_TYPES.DEPARTURE]);
var reservationStatusSchema = z.enum([
  RESERVATION_STATUS.PENDING,
  RESERVATION_STATUS.CONFIRMED,
  RESERVATION_STATUS.ASSIGNED,
  RESERVATION_STATUS.IN_PROGRESS,
  RESERVATION_STATUS.COMPLETED,
  RESERVATION_STATUS.CANCELLED
]);
var paymentStatusSchema = z.enum([
  PAYMENT_STATUS.UNPAID,
  PAYMENT_STATUS.PAID,
  PAYMENT_STATUS.PARTIAL
]);
var paymentMethodSchema = z.enum([
  PAYMENT_METHODS.CASH,
  PAYMENT_METHODS.VENMO,
  PAYMENT_METHODS.ZELLE,
  PAYMENT_METHODS.PAYPAL,
  PAYMENT_METHODS.CHECK,
  PAYMENT_METHODS.BANK_TRANSFER,
  PAYMENT_METHODS.CREDIT_CARD,
  PAYMENT_METHODS.OTHER
]);
var PICKUP_TIME_GRACE_MS = 5 * 60 * 1e3;
var createReservationBaseObject = z.object({
  rideTypeId: z.guid("Please choose a ride type"),
  // Required: the service level is the pricing basis (its rate drives the
  // server-derived `totalCost`). A booking with no service level used to persist
  // a $0 reservation, so it is rejected with a friendly 400 here (QA #198).
  serviceLevelId: z.guid("Please choose a service level"),
  guestEmail: emailSchema,
  guestFirstName: z.string("Please enter a first name").min(1, "Please enter a first name").max(255, "First name must be 255 characters or less"),
  guestLastName: z.string("Please enter a last name").min(1, "Please enter a last name").max(255, "Last name must be 255 characters or less"),
  guestPhone: phoneSchema,
  pickupDatetime: z.iso.datetime({ error: "Please choose a pickup date and time" }).refine((value) => new Date(value).getTime() > Date.now() - PICKUP_TIME_GRACE_MS, {
    message: "Pickup time must be in the future"
  }),
  pickupTimezone: timezoneSchema,
  dropoffDatetime: z.iso.datetime({ error: "Please choose a valid dropoff date and time" }).optional(),
  dropoffTimezone: timezoneSchema.optional(),
  // Legacy single-leg fields — required when `stops` is absent (back-compat).
  // When `stops` is provided these are derived server-side and optional here.
  pickupLocation: z.string("Please enter a pickup address").min(1, "Please enter a pickup address").max(500, "Pickup address must be 500 characters or less").optional(),
  dropoffLocation: z.string("Please enter a dropoff address").min(1, "Please enter a dropoff address").max(500, "Dropoff address must be 500 characters or less").optional(),
  passengerCount: z.number("Please enter the number of passengers").int("Passenger count must be a whole number").min(1, "At least 1 passenger is required").max(50, "Passenger count cannot exceed 50").optional().default(1),
  luggageCount: z.number("Please enter the number of bags").int("Bag count must be a whole number").min(0, "Bag count cannot be negative").max(50, "Bag count cannot exceed 50").optional().default(0),
  carSeatsInfant: z.number("Please enter a car seat count").int("Car seat count must be a whole number").min(0, "Car seat count cannot be negative").max(10, "Car seat count cannot exceed 10").optional().default(0),
  carSeatsToddler: z.number("Please enter a car seat count").int("Car seat count must be a whole number").min(0, "Car seat count cannot be negative").max(10, "Car seat count cannot exceed 10").optional().default(0),
  carSeatsBooster: z.number("Please enter a car seat count").int("Car seat count must be a whole number").min(0, "Car seat count cannot be negative").max(10, "Car seat count cannot exceed 10").optional().default(0),
  wheelchairAccess: z.boolean().optional().default(false),
  petFriendly: z.boolean().optional().default(false),
  specialNotes: z.string().max(2e3, "Notes must be 2,000 characters or less").optional(),
  /**
   * Free-text description of oversized or special luggage (skis, golf bags,
   * wheelchair, extra bags…). Surfaced behind a checkbox gate in the booking
   * flow and shown to the operator as a special request. Optional everywhere.
   */
  luggageDetails: z.string().max(1e3, "Luggage details must be 1,000 characters or less").optional(),
  subType: rideSubTypeSchema.optional(),
  flightNumber: z.string().max(20, "Flight number must be 20 characters or less").optional(),
  airlineCode: z.string().max(50, "Airline must be 50 characters or less").optional(),
  /**
   * Approximate trip duration in hours, used to estimate hourly-rate
   * service levels. Required by the booking UI when the selected service
   * level is priced hourly; optional at the API boundary.
   */
  estimatedDurationHours: z.number("Please enter the approximate duration in hours").min(1, "Duration must be at least 1 hour").max(24, "Duration must be 24 hours or less").optional(),
  /**
   * Where the booking was submitted from. `"widget"` marks a create that
   * originated in the chromeless embeddable widget (`/widget/:slug`), a paid
   * feature (allowEmbeddableWidget — business_plus/business_ultimate). The
   * public create endpoint gates ONLY widget-origin creates on that tier flag
   * (#199); the hosted booking page (`/book/:slug`, available to every tier)
   * omits this field and is never gated. Defaults to `"hosted"`.
   */
  source: z.enum(["hosted", "widget"]).optional().default("hosted"),
  /**
   * Pre-resolved geocoordinates from the frontend autocomplete. When present,
   * the API uses these directly (skipping a geocoding round-trip); distance is
   * always recomputed server-side and coords are never trusted for pricing.
   */
  pickupLat: z.number().min(-90).max(90).optional(),
  pickupLng: z.number().min(-180).max(180).optional(),
  dropoffLat: z.number().min(-90).max(90).optional(),
  dropoffLng: z.number().min(-180).max(180).optional(),
  /**
   * Customer notification channel preferences captured at booking time.
   * At least one must be true (enforced server-side). Defaults: email on,
   * SMS off (matching the Reservation model defaults).
   */
  notifyByEmail: z.boolean().optional().default(true),
  notifyBySms: z.boolean().optional().default(false),
  /**
   * Multi-stop route. When present (≥1 pickup + ≥1 dropoff), these are
   * canonical — `pickupLocation`/`dropoffLocation` are derived server-side
   * from the first pickup / last dropoff address. When absent, the legacy flat
   * fields are required for backward compatibility.
   */
  stops: z.array(stopInputSchema).max(8, "Maximum 8 stops allowed").optional(),
  /**
   * Set when the customer chose "I'm not flying" at an airport endpoint.
   * Flight tracking won't be attempted; surfaces a warning on dispatch.
   */
  noFlightFlag: z.boolean().optional().default(false),
  /**
   * Airport terminal collected when an endpoint stop is detected as an airport.
   * Optional — stored in FlightInfo.terminal.
   */
  terminal: z.string().max(20, "Terminal must be 20 characters or less").optional(),
  /**
   * Optional return leg for round-trip bookings. When present the server creates
   * a second linked reservation using the same passenger info, ride type, and
   * service level as the outbound. Stops default to the reversed outbound stops
   * when omitted.
   */
  returnTrip: z.object({
    pickupDatetime: z.iso.datetime({ error: "Please choose a return pickup date and time" }).refine((value) => new Date(value).getTime() > Date.now() - PICKUP_TIME_GRACE_MS, {
      message: "Return pickup time must be in the future"
    }),
    pickupTimezone: timezoneSchema,
    stops: z.array(stopInputSchema).min(2, "Return trip needs at least one pickup and one dropoff").max(8, "Maximum 8 stops allowed").optional()
  }).optional()
});
var returnTripSchema = createReservationBaseObject.shape.returnTrip.unwrap();
function reservationStopsRefine(data, ctx) {
  if (data.stops && data.stops.length > 0) {
    const pickups = data.stops.filter((s) => s.role === "pickup");
    const dropoffs = data.stops.filter((s) => s.role === "dropoff");
    if (pickups.length < 1) {
      ctx.addIssue({ code: "custom", message: "At least one pickup stop is required", path: ["stops"] });
    }
    if (dropoffs.length < 1) {
      ctx.addIssue({ code: "custom", message: "At least one dropoff stop is required", path: ["stops"] });
    }
    if (pickups.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 pickup stops allowed", path: ["stops"] });
    }
    if (dropoffs.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 dropoff stops allowed", path: ["stops"] });
    }
  } else {
    if (!data.pickupLocation) {
      ctx.addIssue({ code: "custom", message: "Please enter a pickup address", path: ["pickupLocation"] });
    }
    if (!data.dropoffLocation) {
      ctx.addIssue({ code: "custom", message: "Please enter a dropoff address", path: ["dropoffLocation"] });
    }
  }
}
function returnTripRefine(data, ctx) {
  const ret = data.returnTrip;
  if (!ret) return;
  if (data.pickupDatetime && ret.pickupDatetime) {
    if (new Date(ret.pickupDatetime).getTime() <= new Date(data.pickupDatetime).getTime()) {
      ctx.addIssue({
        code: "custom",
        message: "Return pickup must be after the outbound pickup time",
        path: ["returnTrip", "pickupDatetime"]
      });
    }
  }
  if (ret.stops && ret.stops.length > 0) {
    const pickups = ret.stops.filter((s) => s.role === "pickup");
    const dropoffs = ret.stops.filter((s) => s.role === "dropoff");
    if (pickups.length < 1) {
      ctx.addIssue({ code: "custom", message: "Return trip needs at least one pickup stop", path: ["returnTrip", "stops"] });
    }
    if (dropoffs.length < 1) {
      ctx.addIssue({ code: "custom", message: "Return trip needs at least one dropoff stop", path: ["returnTrip", "stops"] });
    }
    if (pickups.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 pickup stops allowed", path: ["returnTrip", "stops"] });
    }
    if (dropoffs.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 dropoff stops allowed", path: ["returnTrip", "stops"] });
    }
  }
}
var createReservationSchema = createReservationBaseObject.superRefine((data, ctx) => {
  reservationStopsRefine(data, ctx);
  returnTripRefine(data, ctx);
});
var updateReservationSchema = z.object({
  serviceLevelId: uuidSchema.nullable().optional(),
  driverUserId: uuidSchema.nullable().optional(),
  vehicleId: uuidSchema.nullable().optional(),
  pickupDatetime: z.iso.datetime().optional(),
  pickupTimezone: timezoneSchema.optional(),
  dropoffDatetime: z.iso.datetime().nullable().optional(),
  dropoffTimezone: timezoneSchema.nullable().optional(),
  pickupLocation: z.string().min(1).max(500).optional(),
  dropoffLocation: z.string().min(1).max(500).optional(),
  passengerCount: z.number().int().min(1).max(50).optional(),
  luggageCount: z.number().int().min(0).max(50).optional(),
  carSeatsInfant: z.number().int().min(0).max(10).optional(),
  carSeatsToddler: z.number().int().min(0).max(10).optional(),
  carSeatsBooster: z.number().int().min(0).max(10).optional(),
  wheelchairAccess: z.boolean().optional(),
  petFriendly: z.boolean().optional(),
  specialNotes: z.string().max(2e3).nullable().optional(),
  luggageDetails: z.string().max(1e3).nullable().optional(),
  status: reservationStatusSchema.optional(),
  cancellationReason: z.string().max(255).nullable().optional(),
  totalCost: z.number().min(0).nullable().optional(),
  paymentStatus: paymentStatusSchema.optional(),
  paymentMethod: paymentMethodSchema.nullable().optional(),
  amountPaid: z.number().min(0).nullable().optional(),
  tipAmount: z.number().min(0).nullable().optional(),
  paidAt: z.iso.datetime().nullable().optional(),
  paymentNotes: z.string().max(500).nullable().optional(),
  quoteExpiresAt: z.iso.datetime().nullable().optional()
});
var assignDriverSchema = z.object({
  driverUserId: uuidSchema,
  vehicleId: uuidSchema.nullable().optional()
});
var updatePaymentSchema = z.object({
  paymentStatus: paymentStatusSchema,
  paymentMethod: paymentMethodSchema.optional(),
  amountPaid: z.number().min(0).optional(),
  tipAmount: z.number().min(0).nullable().optional(),
  paidAt: z.iso.datetime().nullable().optional(),
  paymentNotes: z.string().max(500).optional()
});
var createVehicleSchema = z.object({
  make: z.string().min(1).max(100),
  model: z.string().min(1).max(100),
  year: z.number().int().min(1900).max(2100).nullable().optional(),
  color: z.string().max(50).nullable().optional(),
  licensePlate: z.string().max(20).nullable().optional(),
  vin: z.string().max(17).nullable().optional(),
  capacity: z.number().int().min(1).max(100).nullable().optional(),
  // Accessibility & amenity capabilities (dispatch vehicle-fit, epic #522 U4).
  wheelchairAccessible: z.boolean().optional().default(false),
  petFriendly: z.boolean().optional().default(false),
  childSeatCapable: z.boolean().optional().default(false),
  luggageCapacity: z.number().int().min(0).max(100).nullable().optional(),
  isDefault: z.boolean().optional().default(false)
});
var updateVehicleSchema = createVehicleSchema.partial().extend({
  wheelchairAccessible: z.boolean().optional(),
  petFriendly: z.boolean().optional(),
  childSeatCapable: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  status: z.enum(["active", "retired"]).optional()
});
var reservationFiltersSchema = z.object({
  status: reservationStatusSchema.optional(),
  driverUserId: uuidSchema.optional(),
  serviceLevelId: uuidSchema.optional(),
  fromDate: z.iso.datetime().optional(),
  toDate: z.iso.datetime().optional(),
  search: z.string().max(100).optional()
}).extend(paginationSchema.shape);
var documentTypeSchema = z.enum([
  DOCUMENT_TYPES.DRIVER_LICENSE,
  DOCUMENT_TYPES.INSURANCE,
  DOCUMENT_TYPES.VEHICLE_REGISTRATION,
  DOCUMENT_TYPES.OTHER
]);
var documentAccessStatusSchema = z.enum([
  DOCUMENT_ACCESS_STATUS.PENDING,
  DOCUMENT_ACCESS_STATUS.GRANTED,
  DOCUMENT_ACCESS_STATUS.DENIED,
  DOCUMENT_ACCESS_STATUS.REVOKED
]);
var uploadDocumentSchema = z.object({
  documentType: documentTypeSchema,
  expiresAt: z.iso.datetime().optional(),
  metadata: z.record(z.string(), z.unknown()).optional()
});
var requestDocumentAccessSchema = z.object({
  documentId: uuidSchema,
  notes: z.string().max(500).optional()
});
var respondToAccessRequestSchema = z.object({
  status: z.enum([DOCUMENT_ACCESS_STATUS.GRANTED, DOCUMENT_ACCESS_STATUS.DENIED]),
  notes: z.string().max(500).optional()
});
var reviewDocumentSchema = z.object({
  reviewStatus: z.enum([DOCUMENT_REVIEW_STATUS.APPROVED, DOCUMENT_REVIEW_STATUS.REJECTED]),
  reviewReason: z.string().trim().min(1).max(500).optional()
}).refine((d) => d.reviewStatus !== DOCUMENT_REVIEW_STATUS.REJECTED || Boolean(d.reviewReason), {
  message: "A reason is required when rejecting a document",
  path: ["reviewReason"]
});
var driverApprovalStatusSchema = z.enum([
  DRIVER_APPROVAL_STATUS.PENDING_DOCUMENTS,
  DRIVER_APPROVAL_STATUS.DOCUMENTS_RECEIVED,
  DRIVER_APPROVAL_STATUS.APPROVED,
  DRIVER_APPROVAL_STATUS.REJECTED,
  DRIVER_APPROVAL_STATUS.MORE_INFO_NEEDED
]);
var updateDriverApprovalSchema = z.object({
  approvalStatus: driverApprovalStatusSchema,
  notes: z.string().max(1e3).optional()
});
var availabilityStatusSchema = z.enum([
  AVAILABILITY_STATUS.AVAILABLE,
  AVAILABILITY_STATUS.UNAVAILABLE,
  AVAILABILITY_STATUS.BUSY
]);
var recurrenceRuleSchema = z.object({
  frequency: z.enum(["daily", "weekly", "monthly"]),
  interval: z.number().int().min(1).max(12),
  byDay: z.array(z.string()).optional(),
  until: z.iso.datetime().optional()
});
var createAvailabilitySchema = z.object({
  startDatetime: z.iso.datetime(),
  endDatetime: z.iso.datetime(),
  timezone: timezoneSchema,
  status: availabilityStatusSchema,
  reason: z.string().max(255).optional(),
  isRecurring: z.boolean().optional().default(false),
  recurrenceRule: recurrenceRuleSchema.optional()
});
var updateAvailabilitySchema = createAvailabilitySchema.partial();
var createSavedAddressSchema = z.object({
  label: z.string().min(1).max(100),
  addressLine1: z.string().min(1).max(255),
  addressLine2: z.string().max(255).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(50).optional(),
  postalCode: z.string().max(20).optional(),
  country: z.string().max(100).optional().default("USA"),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  isDefault: z.boolean().optional().default(false)
});
var updateSavedAddressSchema = createSavedAddressSchema.partial();
var clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be in HH:MM (24-hour) format");
var availabilitySlotSchema = z.object({
  start: clockTimeSchema,
  end: clockTimeSchema
}).refine((slot) => slot.end !== slot.start, {
  message: "End time must be different from start time",
  path: ["end"]
});
var dayAvailabilitySchema = z.array(availabilitySlotSchema);
var weeklyAvailabilitySchema = z.object({
  monday: dayAvailabilitySchema,
  tuesday: dayAvailabilitySchema,
  wednesday: dayAvailabilitySchema,
  thursday: dayAvailabilitySchema,
  friday: dayAvailabilitySchema,
  saturday: dayAvailabilitySchema,
  sunday: dayAvailabilitySchema
});
var MINUTES_PER_DAY = 1440;
var MAX_MINUTE = MINUTES_PER_DAY - 1;
var WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
function minuteOf(time) {
  const [h, m] = time.split(":");
  return (parseInt(h ?? "0", 10) || 0) * 60 + (parseInt(m ?? "0", 10) || 0);
}
function toIntervals(start, end) {
  if (start === end) return [];
  if (end > start) return [[start, end]];
  return [
    [start, MINUTES_PER_DAY],
    [0, end]
  ];
}
function slotsOverlapMinutes(a, b) {
  const aIntervals = toIntervals(minuteOf(a.start), minuteOf(a.end));
  const bIntervals = toIntervals(minuteOf(b.start), minuteOf(b.end));
  return aIntervals.some(([as, ae]) => bIntervals.some(([bs, be]) => as < be && bs < ae));
}
function findOverlappingDay(week) {
  for (const day of WEEKDAY_KEYS) {
    const slots = week[day];
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const a = slots[i];
        const b = slots[j];
        if (a && b && slotsOverlapMinutes(a, b)) return day;
      }
    }
  }
  return null;
}
var updateBrandingSchema = z.object({
  logoUrl: z.url().max(500).nullable().optional(),
  primaryColor: hexColorSchema.optional()
});
var loginSchema = z.object({
  email: z.email("Invalid email address").max(255),
  password: z.string().min(8, "Password must be at least 8 characters").max(128)
});
var certificationFieldsSchema = z.object({
  certifiedLicensed: z.literal(true, {
    error: "You must certify that you hold a valid driver's license"
  }),
  certifiedInsured: z.literal(true, {
    error: "You must certify that you have commercial auto insurance"
  }),
  certifiedAuthorized: z.literal(true, {
    error: "You must certify that you are authorized to operate"
  }),
  termsAcceptedAt: z.iso.datetime()
  // Covers driver terms + indemnification
});
var driverCertificationInfoSchema = certificationFieldsSchema;
var driverRegistrationInfoSchema = certificationFieldsSchema;
var upgradeDriverPlanSchema = z.object({
  plan: z.literal("paid_driver")
});
var downgradeDriverPlanSchema = z.object({
  plan: z.literal("free_driver")
});
var createBusinessFromDriverSchema = z.object({
  companyName: z.string().min(1, "Company name is required").max(255),
  phone: phoneSchema.optional(),
  address: z.string().max(500).optional()
});
var internationalAddressSchema = z.object({
  addressLine1: z.string().min(1, "Street address is required").max(255),
  addressLine2: z.string().max(255).optional(),
  city: z.string().min(1, "City is required").max(100),
  stateOrRegion: z.string().max(100).optional(),
  // Not required for all countries
  postalCode: z.string().max(20).optional(),
  // Not required for all countries
  country: z.string().min(2, "Country is required").max(100).default("United States")
});
var addressSchema = internationalAddressSchema;
var DRIVER_REQUIREMENT_DOCUMENT_TYPES = [
  "driver_license",
  "insurance",
  "vehicle_registration",
  "background_check"
];
var DEFAULT_REQUIRED_DOCUMENT_TYPES = [
  "driver_license",
  "insurance"
];
var driverRequirementsSchema = z.object({
  requiredDocumentTypes: z.array(z.enum(DRIVER_REQUIREMENT_DOCUMENT_TYPES)).max(DRIVER_REQUIREMENT_DOCUMENT_TYPES.length).default([...DEFAULT_REQUIRED_DOCUMENT_TYPES]),
  requireVehicleInfo: z.boolean().default(false),
  autoRequestDriverDocuments: z.boolean().default(false)
});
var COMMON_LOCATION_LABEL_MAX = 120;
var COMMON_LOCATION_ADDRESS_MAX = 255;
var MAX_COMMON_LOCATIONS = 50;
var commonLocationSchema = z.object({
  label: z.string().min(1, "Name is required").max(COMMON_LOCATION_LABEL_MAX),
  address: z.string().min(1, "Address is required").max(COMMON_LOCATION_ADDRESS_MAX)
});
var commonLocationsSchema = z.object({
  locations: z.array(commonLocationSchema).max(MAX_COMMON_LOCATIONS).default([])
});
var companyTierSchema = z.enum([
  "business",
  "business_plus",
  "business_ultimate"
]);
var companyRegistrationInfoSchema = z.object({
  companyName: z.string().min(1, "Company name is required").max(255),
  phone: phoneSchema,
  address: addressSchema.optional().nullable(),
  fleetSize: z.enum(["1", "2-5", "6-10", "11-25", "26+"]).optional().nullable(),
  termsAcceptedAt: z.iso.datetime(),
  // The company tier the applicant chose during registration. Persisted so the
  // approval modal can default to (and an admin can confirm/override) it, instead
  // of silently forcing every company onto Business (#297). Optional/nullable for
  // backward-compatibility with pending rows registered before it was captured.
  selectedPlan: companyTierSchema.optional().nullable(),
  // What the company will require from every driver (chosen at onboarding).
  driverRequirements: driverRequirementsSchema.optional()
});
var reapplyBusinessSchema = companyRegistrationInfoSchema.extend({
  selectedPlan: companyTierSchema
});
var baseRegisterSchema = z.object({
  email: z.email("Invalid email address").max(255),
  password: z.string().min(8, "Password must be at least 8 characters").max(128),
  name: z.string().min(1, "Name is required").max(255),
  phone: z.union([
    z.literal(""),
    phoneSchema
  ]).optional().transform((val) => val === "" ? void 0 : val),
  /**
   * When true, creates a solo driver account with:
   * - Auto-created company in the driver's name
   * - Driver role only (no admin access)
   * - Free driver tier (can upgrade later)
   * - Requires additional driver fields (license, insurance)
   * When false or undefined, creates a customer account (no company).
   */
  registerAsDriver: z.boolean().optional().default(false),
  /**
   * When true, creates a company admin account that:
   * - Requires platform admin approval
   * - Once approved, creates a new company with admin role
   * - Requires additional company fields
   */
  registerAsCompanyAdmin: z.boolean().optional().default(false)
});
var driverRegisterFieldsSchema = z.object({
  selectedPlan: z.enum(["free_driver", "paid_driver"]).optional(),
  certifiedLicensed: z.boolean().optional(),
  certifiedInsured: z.boolean().optional(),
  certifiedAuthorized: z.boolean().optional(),
  agreedToDriverTerms: z.boolean().optional()
});
var companyAdminRegisterFieldsSchema = z.object({
  companyName: z.string().min(1).max(255).optional(),
  businessAddress: z.string().max(500).optional().nullable(),
  fleetSize: z.enum(["1", "2-5", "6-10", "11-25", "26+"]).optional().nullable(),
  agreedToCompanyTerms: z.boolean().optional()
});
var registerSchema = baseRegisterSchema.extend(driverRegisterFieldsSchema.shape).extend(companyAdminRegisterFieldsSchema.shape).superRefine((data, ctx) => {
  if (data.registerAsDriver && data.registerAsCompanyAdmin) {
    ctx.addIssue({
      code: "custom",
      message: "Cannot register as both driver and company admin",
      path: ["registerAsDriver"]
    });
    return;
  }
  if (data.registerAsDriver) {
    if (!data.selectedPlan) {
      ctx.addIssue({
        code: "custom",
        message: "Please select a plan",
        path: ["selectedPlan"]
      });
    }
    if (!data.certifiedLicensed) {
      ctx.addIssue({
        code: "custom",
        message: "You must certify that you hold a valid driver's license",
        path: ["certifiedLicensed"]
      });
    }
    if (!data.certifiedInsured) {
      ctx.addIssue({
        code: "custom",
        message: "You must certify that you have commercial auto insurance",
        path: ["certifiedInsured"]
      });
    }
    if (!data.certifiedAuthorized) {
      ctx.addIssue({
        code: "custom",
        message: "You must certify that you are authorized to operate",
        path: ["certifiedAuthorized"]
      });
    }
    if (!data.agreedToDriverTerms) {
      ctx.addIssue({
        code: "custom",
        message: "You must agree to the Driver Terms of Service",
        path: ["agreedToDriverTerms"]
      });
    }
  }
  if (data.registerAsCompanyAdmin) {
    if (!data.companyName) {
      ctx.addIssue({
        code: "custom",
        message: "Company name is required",
        path: ["companyName"]
      });
    }
    if (!data.phone) {
      ctx.addIssue({
        code: "custom",
        message: "Phone number is required for company registration",
        path: ["phone"]
      });
    }
    if (!data.agreedToCompanyTerms) {
      ctx.addIssue({
        code: "custom",
        message: "You must agree to the Company Terms",
        path: ["agreedToCompanyTerms"]
      });
    }
  }
});
var forgotPasswordSchema = z.object({
  email: z.email("Invalid email address").max(255)
});
var resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8).max(128)
});
var publicQuoteSchema = z.object({
  companyId: uuidSchema,
  rideTypeId: uuidSchema,
  serviceLevelId: uuidSchema.optional(),
  // Stops mode (multi-stop): coords are preferred for faster pricing.
  stops: z.array(stopInputSchema).max(8).optional(),
  // Legacy mode: flat pickup/dropoff (required when stops absent).
  pickupLocation: z.string().min(1).max(500).optional(),
  dropoffLocation: z.string().min(1).max(500).optional(),
  pickupDatetime: z.iso.datetime(),
  pickupTimezone: timezoneSchema,
  passengerCount: z.number().int().min(1).max(50).optional().default(1)
});
var flightLookupSchema = z.object({
  companyId: uuidSchema,
  flightNumber: z.string("Please enter a flight number").min(1, "Please enter a flight number").max(20, "Flight number must be 20 characters or less"),
  airlineCode: z.string().max(50, "Airline must be 50 characters or less").optional()
});
var inquiryStatusSchema = z.enum([
  INQUIRY_STATUS.NEW,
  INQUIRY_STATUS.CONTACTED,
  INQUIRY_STATUS.CONVERTED,
  INQUIRY_STATUS.CLOSED
]);
var inquiryPlanOptionSchema = z.enum([
  INQUIRY_PLAN_OPTIONS.BUSINESS,
  INQUIRY_PLAN_OPTIONS.BUSINESS_PLUS,
  INQUIRY_PLAN_OPTIONS.BUSINESS_ULTIMATE,
  INQUIRY_PLAN_OPTIONS.HELP_ME_CHOOSE
]);
var fleetSizeSchema = z.enum([
  FLEET_SIZES.ONE,
  FLEET_SIZES.TWO_TO_FIVE,
  FLEET_SIZES.SIX_TO_TEN,
  FLEET_SIZES.ELEVEN_TO_TWENTYFIVE,
  FLEET_SIZES.TWENTYSIX_PLUS
]);
var createInquirySchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  email: emailSchema,
  phone: phoneSchema,
  companyName: z.string().min(1, "Company name is required").max(255),
  desiredPlan: inquiryPlanOptionSchema,
  fleetSize: fleetSizeSchema.optional(),
  country: z.string().min(1, "Country is required").max(100)
});
var updateInquirySchema = z.object({
  status: inquiryStatusSchema.optional(),
  notes: z.string().max(2e3).optional().nullable()
});
var listInquiriesSchema = z.object({
  status: inquiryStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0)
});
var GUEST_MESSAGE_MAX_LENGTH = 2e3;
var guestMessageBodySchema = z.object({
  body: z.string().trim().min(1, "Message cannot be empty").max(GUEST_MESSAGE_MAX_LENGTH, "Message cannot exceed 2000 characters")
});
var ADMIN_MESSAGE_MAX_LENGTH = 1e3;
var ADMIN_MESSAGE_MAX_LENGTH_ERROR = `Message exceeds ${String(
  ADMIN_MESSAGE_MAX_LENGTH
)} characters`;
var SETUP_GUIDE_STEP_KEYS = [
  "services",
  "driverRequirements",
  "branding",
  "team",
  "vehicles",
  "locations",
  "payments",
  "widget",
  "firstBooking"
];
var setupGuideStepKeySchema = z.enum(SETUP_GUIDE_STEP_KEYS);
var setupGuidePatchSchema = z.object({
  acknowledge: setupGuideStepKeySchema.optional(),
  dismissed: z.boolean().optional()
}).strict();

export { ADMIN_MESSAGE_MAX_LENGTH, ADMIN_MESSAGE_MAX_LENGTH_ERROR, COMMON_LOCATION_ADDRESS_MAX, COMMON_LOCATION_LABEL_MAX, COMPANY_BOOKING_POLICY_DEFAULTS, COMPANY_LOCATION_SETTINGS_DEFAULTS, DEFAULT_REQUIRED_DOCUMENT_TYPES, DRIVER_REQUIREMENT_DOCUMENT_TYPES, GUEST_MESSAGE_MAX_LENGTH, MAX_COMMON_LOCATIONS, MAX_MINUTE, MINUTES_PER_DAY, PICKUP_TIME_GRACE_MS, SERVICE_LEVEL_FEATURE_KEYS, SERVICE_LEVEL_MAX_LUGGAGE, SERVICE_LEVEL_MAX_PASSENGERS, SERVICE_LEVEL_MAX_PRICE, SERVICE_LEVEL_MIN_PASSENGERS, SETUP_GUIDE_STEP_KEYS, addressSchema, assignDriverSchema, availabilitySlotSchema, availabilityStatusSchema, commonLocationSchema, commonLocationsSchema, companyBookingPolicySchema, companyLocationSettingsSchema, companyRegistrationInfoSchema, companyRoleSchema, companySettingsSchema, companyTierSchema, createAvailabilitySchema, createBusinessFromDriverSchema, createCompanyMemberSchema, createCompanySchema, createInquirySchema, createInvitationSchema, createReservationSchema, createSavedAddressSchema, createServiceLevelSchema, createUserSchema, createVehicleSchema, documentAccessStatusSchema, documentTypeSchema, downgradeDriverPlanSchema, driverApprovalStatusSchema, driverCertificationInfoSchema, driverRegistrationInfoSchema, driverRequirementsSchema, emailSchema, embedOriginSchema, findOverlappingDay, fleetSizeSchema, flightLookupSchema, forgotPasswordSchema, guestMessageBodySchema, hexColorSchema, inquiryPlanOptionSchema, inquiryStatusSchema, internationalAddressSchema, invitationMetadataSchema, invitationStatusSchema, listInquiriesSchema, listInvitationsSchema, loginSchema, paginationSchema, paymentMethodSchema, paymentStatusSchema, phoneSchema, publicQuoteSchema, reapplyBusinessSchema, recurrenceRuleSchema, registerSchema, requestDocumentAccessSchema, reservationFiltersSchema, reservationStatusSchema, reservationStopsRefine, resetPasswordSchema, resolveBookingPolicy, resolveLocationSettings, respondToAccessRequestSchema, returnTripRefine, returnTripSchema, reviewDocumentSchema, rideSubTypeSchema, rideTypeSchema, serviceLevelFeaturesObjectSchema, serviceLevelFeaturesSchema, serviceLevelRestrictionsSchema, serviceLevelSchema, serviceLevelUpdateSchema, setupGuidePatchSchema, setupGuideStepKeySchema, slotsOverlapMinutes, slugSchema, stopInputSchema, stopRoleSchema, timezoneSchema, updateAvailabilitySchema, updateBrandingSchema, updateCompanyMemberSchema, updateCompanyRideTypesSchema, updateCompanySchema, updateCompanySlugSchema, updateDriverApprovalSchema, updateInquirySchema, updatePaymentSchema, updateReservationSchema, updateSavedAddressSchema, updateServiceLevelSchema, updateUserSchema, updateVehicleSchema, upgradeDriverPlanSchema, uploadDocumentSchema, uuidSchema, weeklyAvailabilitySchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map